import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { QrCode, ShieldAlert, Smartphone, Check, X } from 'lucide-react';
import { useLang } from './i18n';

type Api = (url: string, method?: string, body?: unknown) => Promise<any>;

// Login page: shows a QR code that a signed-in phone can scan and approve (like Discord).
export function QrLogin({ request, onDone }: { request: Api; onDone: () => Promise<void> | void }) {
  const [, , tr] = useLang();
  const [img, setImg] = useState(''), [state, setState] = useState<'loading' | 'waiting' | 'scanned' | 'denied' | 'error'>('loading'), [who, setWho] = useState(''), [round, setRound] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    let stop = false, timer = 0;
    (async () => {
      try {
        const q = await request('/auth/qr', 'POST', {});
        if (stop) return;
        setImg(await QRCode.toDataURL(location.origin + '/?qr=' + q.id, { width: 360, margin: 1, color: { dark: '#000000', light: '#ffffff' } }));
        setState('waiting');
        const poll = async () => {
          if (stop) return;
          try {
            const r = await fetch('/api/auth/qr/' + q.id + '?secret=' + encodeURIComponent(q.secret), { credentials: 'same-origin', cache: 'no-store' }).then(x => x.json());
            if (stop) return;
            if (r.state === 'approved') { done.current = true; await onDone(); return; }
            if (r.state === 'expired') { setRound(x => x + 1); return; }
            if (r.state === 'denied') { setState('denied'); timer = window.setTimeout(() => setRound(x => x + 1), 3000); return; }
            if (r.state === 'scanned') { setState('scanned'); setWho(r.user?.displayName || ''); }
          } catch {}
          timer = window.setTimeout(poll, 2000);
        };
        timer = window.setTimeout(poll, 2000);
      } catch { if (!stop) { setState('error'); timer = window.setTimeout(() => setRound(x => x + 1), 10000); } }
    })();
    return () => { stop = true; clearTimeout(timer); };
  }, [round]);
  return <div className="qr-login">
    <div className={'qr-login-code' + (state === 'scanned' ? ' is-scanned' : '')}>
      {img && state !== 'error' ? <img src={img} alt={tr('QR code for sign-in', 'QR-Code zur Anmeldung')} /> : <QrCode size={48} />}
      {state === 'scanned' && <div className="qr-login-overlay"><Smartphone size={34} /><b>{who}</b></div>}
    </div>
    <h3>{tr('Sign in with QR code', 'Mit QR-Code anmelden')}</h3>
    <p>{state === 'scanned' ? tr('Confirm the sign-in on your phone.', 'Bestätige die Anmeldung auf deinem Handy.') : state === 'denied' ? tr('The sign-in was declined.', 'Die Anmeldung wurde abgelehnt.') : state === 'error' ? tr('QR sign-in is unavailable right now.', 'QR-Anmeldung ist gerade nicht verfügbar.') : tr('Scan this with the camera of a phone that is signed in to MALVO.', 'Scanne den Code mit der Kamera eines Handys, auf dem du bei MALVO angemeldet bist.')}</p>
  </div>;
}

const KEY = 'malvo-qr-approve';
// Remember a scanned QR link across the sign-in on the phone, and remove it from the address bar.
export function captureQrParam() {
  try { const p = new URLSearchParams(location.search), id = p.get('qr'); if (id && /^[A-Za-z0-9_-]{8,40}$/.test(id)) { sessionStorage.setItem(KEY, id); p.delete('qr'); history.replaceState({}, '', location.pathname + (p.size ? '?' + p : '') + location.hash); } } catch {}
}
export function pendingQr() { try { return sessionStorage.getItem(KEY); } catch { return null; } }

// Phone: confirm or decline the sign-in of the other device.
export function QrApprove({ request, onClose }: { request: Api; onClose: () => void }) {
  const [, , tr] = useLang();
  const id = pendingQr();
  const [info, setInfo] = useState<{ device: string; ip: string } | null>(null), [error, setError] = useState(''), [busy, setBusy] = useState(false), [result, setResult] = useState<'' | 'ok' | 'no'>('');
  const close = () => { try { sessionStorage.removeItem(KEY); } catch {} onClose(); };
  useEffect(() => { if (!id) { onClose(); return; } request('/auth/qr/' + id + '/info').then(setInfo).catch(e => setError(e.message)); }, []);
  const answer = async (approve: boolean) => { setBusy(true); try { await request('/auth/qr/' + id, 'POST', { approve }); setResult(approve ? 'ok' : 'no'); setTimeout(close, 1800); } catch (e) { setError((e as Error).message); } finally { setBusy(false); } };
  return <div className="qr-approve-backdrop" role="dialog" aria-modal="true" aria-label={tr('Sign in on another device', 'Auf anderem Gerät anmelden')}>
    <section className="qr-approve">
      <Smartphone size={30} className="qr-approve-icon" />
      <h2>{tr('Sign in on another device?', 'Auf einem anderen Gerät anmelden?')}</h2>
      {result === 'ok' ? <p className="qr-ok"><Check size={16} /> {tr('Done. The other device is now signed in.', 'Fertig. Das andere Gerät ist jetzt angemeldet.')}</p>
        : result === 'no' ? <p>{tr('Declined.', 'Abgelehnt.')}</p>
        : error ? <><p className="qr-warn">{error}</p><button className="secondary" onClick={close}>{tr('Close', 'Schließen')}</button></>
        : info ? <>
          <div className="qr-device"><b>{info.device}</b><small>IP {info.ip}</small></div>
          <p className="qr-warn"><ShieldAlert size={15} /> {tr('Only confirm if you are signing in yourself right now. Never scan a QR code someone else sent you – they would get full access to your account.', 'Bestätige nur, wenn du dich gerade selbst anmeldest. Scanne nie einen QR-Code, den dir jemand geschickt hat – diese Person hätte sonst vollen Zugriff auf dein Konto.')}</p>
          <div className="qr-actions"><button className="secondary" disabled={busy} onClick={() => void answer(false)}><X size={15} /> {tr('Decline', 'Ablehnen')}</button><button disabled={busy} onClick={() => void answer(true)}><Check size={15} /> {tr('Yes, sign in', 'Ja, anmelden')}</button></div>
        </> : <p>{tr('Loading…', 'Lädt …')}</p>}
    </section>
  </div>;
}
